import type {
	Filter_Checkbox_List,
	Filter_Custom,
	Filter_Section_Filter_Type_Item_Union,
	Filter_Slider,
	Filter_Tag_List,
	Filter_Tags,
	Filter_Toggle
} from '../../gql/types';
import type { FilterCollection } from './constants';
import type {
	ColorFragment,
	Filter_Section_Filter_TypeFragment,
	FilterFragment
} from './queries/__generated__/fragments.generated';

type IsAny<T> = 0 extends 1 & T ? true : false;

/**
 * Filter sections come from the API, but most sites also build them by hand:
 * a gallery assembles its sections from its own data, leaving out the fields
 * it has no value for and tagging each object with its GraphQL type name. The
 * generated fragment types describe only the fetched shape (every nullable
 * field a required key, no `__typename`), so this widens them to accept both:
 * nullable fields become optional and `__typename` is allowed, at any depth.
 */
type FetchedOrBuilt<T> =
	IsAny<T> extends true
		? T
		: T extends readonly (infer Item)[]
			? FetchedOrBuilt<Item>[]
			: T extends object
				? {
						[Key in keyof T as null extends T[Key] ? never : Key]: FetchedOrBuilt<T[Key]>;
					} & {
						[Key in keyof T as null extends T[Key] ? Key : never]?: FetchedOrBuilt<T[Key]>;
					} & { __typename?: string }
				: T;

// `undefined` too: callers pass an optional-chained value straight through.
export type FilterSections = FetchedOrBuilt<FilterFragment['filter_section']> | undefined;

export type FilterSection = NonNullable<FilterSections>[0];

export type FilterSectionFilterType = FetchedOrBuilt<Filter_Section_Filter_TypeFragment>;

export type FilterSectionFilterTypeItem = Filter_Section_Filter_Type_Item_Union;

export type FilterCustom = Filter_Custom;

export type FilterSlider = Filter_Slider;

export type FilterToggle = Filter_Toggle;

export type FilterTagList = Filter_Tag_List;

export type FilterTag = Filter_Tags;

export type FilterCheckboxList = Filter_Checkbox_List;

export type FilterDrawerCollectionRestrictedValues =
	string | number | boolean | string[] | number[] | boolean;

export interface FilterDrawerCollection<T = FilterDrawerCollectionRestrictedValues> {
	filterCollectionId: string;
	filterCollection?: FilterCollection;
	value: T;
	inclusive?: boolean;
	filterItem?: Filter_Section_Filter_Type_Item_Union;
}

export type FilterDrawerState = Record<'sort' | string, FilterDrawerCollection>;

export type FilterDrawerTouchedItems = Record<string, true>;

export interface FilterSortOption {
	dataCy: string;
	value: string;
	label: string;
}

export type SpinColor = ColorFragment;

export type GetCustomFilterAttribute = (options: {
	value: FilterDrawerCollectionRestrictedValues;
}) => object | null;

export type FilterDrawerCustomAttributes = Record<string, GetCustomFilterAttribute>;

export enum FilterSliderValuesLabelVariant {
	FullWidth = 'full_width'
}

/**
 * Search params the filter drawer only reads. SvelteKit 3 types `page.url.searchParams`
 * as readonly; omitting the mutators lets both SvelteKit 2's `URLSearchParams` and
 * SvelteKit 3's readonly params through.
 */
export type ReadonlySearchParams = Omit<URLSearchParams, 'set' | 'append' | 'delete' | 'sort'>;
